import { AuthenticatedFileCache, BlobFetcher } from "./AuthenticatedFileCache";
import { PAGE_FILE_SRC_ATTRIBUTE, isPageFileUrl, isViewableInBrowser, pageFileName } from "./PageFileUrl";

import { HttpClient } from "../http/HttpClient";

export { PAGE_FILE_SRC_ATTRIBUTE };

/** The default fetcher: the same MSAL bearer as every other Ark.Core call. */
export const fetchWithBearer: BlobFetcher = async (url) => {
    const res = await HttpClient.sessionRequest<Blob>({ method: "GET", url, responseType: "blob" });
    return res.data;
};

/**
 * Resolves attachment images and links anywhere in the document (OrbitalPlan/18 phase 0: `PageFiles` needs a token):
 * - `<img data-ark-src>` gets `src` = an object URL from the shared cache; the image is released when it leaves the DOM;
 * - a click on `<a href>` to an attachment fetches it with the token and opens it in a new tab (images, PDF, text) or
 *   downloads it under its own name.
 * One MutationObserver on the document covers the page view, the history popup, the editor previews and the media popup.
 */
export class PageFileResolver {
    private static global?: PageFileResolver;

    /** The app-wide instance (started by `Startup`). */
    static get Global(): PageFileResolver {
        if (!PageFileResolver.global) PageFileResolver.global = new PageFileResolver(new AuthenticatedFileCache(fetchWithBearer));
        return PageFileResolver.global;
    }

    private readonly held = new WeakMap<Element, string>();
    private observer?: MutationObserver;
    private clickRoot?: Document;

    constructor(
        readonly cache: AuthenticatedFileCache,
        private readonly isPageFile: (url: string) => boolean = (url) => isPageFileUrl(url)
    ) {}

    /** Resolves what is already in `root` and keeps watching it; links are intercepted on `root`'s document. */
    start(root: Element = document.body ?? document.documentElement): void {
        if (this.observer) return;
        this.scan(root);
        this.observer = new MutationObserver((records) => this.onMutations(records));
        this.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: [PAGE_FILE_SRC_ATTRIBUTE] });
        this.clickRoot = root.ownerDocument ?? document;
        this.clickRoot.addEventListener("click", this.onClick, true);
    }

    stop(): void {
        this.observer?.disconnect();
        this.observer = undefined;
        this.clickRoot?.removeEventListener("click", this.onClick, true);
        this.clickRoot = undefined;
    }

    /** Resolves every `img[data-ark-src]` in (and including) `root`. */
    scan(root: ParentNode): void {
        if (root instanceof Element && root.hasAttribute(PAGE_FILE_SRC_ATTRIBUTE)) this.resolve(root);
        root.querySelectorAll?.(`[${PAGE_FILE_SRC_ATTRIBUTE}]`).forEach((el) => this.resolve(el));
    }

    /** Points one element at the object URL of its `data-ark-src`; a URL that is not an Ark attachment is used as is. */
    resolve(el: Element): void {
        const wanted = el.getAttribute(PAGE_FILE_SRC_ATTRIBUTE);
        const held = this.held.get(el);
        if (held === wanted) return;
        if (held) {
            this.held.delete(el);
            this.cache.release(held);
        }
        if (!wanted) return;
        if (!this.isPageFile(wanted)) {
            el.setAttribute("src", wanted);
            return;
        }

        this.held.set(el, wanted);
        el.classList.add("ark-file-loading");
        this.cache.acquire(wanted).then(
            (objectUrl) => {
                if (this.held.get(el) !== wanted) return; // re-pointed or removed meanwhile (already released)
                el.setAttribute("src", objectUrl);
                el.classList.remove("ark-file-loading");
            },
            (error) => {
                if (this.held.get(el) !== wanted) return;
                this.held.delete(el); // the cache forgot the failed entry: nothing to release
                el.classList.remove("ark-file-loading");
                el.classList.add("ark-file-error");
                console.warn("Could not load attachment", wanted, error?.message ?? error);
            }
        );
    }

    /** Releases every resolved element in (and including) `root`. */
    releaseIn(root: Node): void {
        if (!(root instanceof Element)) return;
        const release = (el: Element) => {
            const held = this.held.get(el);
            if (!held) return;
            this.held.delete(el);
            this.cache.release(held);
        };
        release(root);
        root.querySelectorAll(`[${PAGE_FILE_SRC_ATTRIBUTE}]`).forEach(release);
    }

    /** Fetches an attachment with the token and opens or downloads it. `win` is a tab opened synchronously in the click. */
    async open(url: string, win: Window | null): Promise<void> {
        try {
            const objectUrl = await this.cache.acquire(url);
            this.cache.release(url);
            if (win && isViewableInBrowser(url)) {
                win.location.href = objectUrl;
                return;
            }
            win?.close();
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = pageFileName(url);
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error: any) {
            win?.close();
            console.warn("Could not open attachment", url, error?.message ?? error);
        }
    }

    private onMutations(records: MutationRecord[]): void {
        for (const record of records) {
            if (record.type === "attributes") {
                if (record.target instanceof Element) this.resolve(record.target);
                continue;
            }
            record.removedNodes.forEach((node) => {
                // a node moved within the document is reported removed and added: only release what really left
                if (!node.isConnected) this.releaseIn(node);
            });
            record.addedNodes.forEach((node) => {
                if (node instanceof Element) this.scan(node);
            });
        }
    }

    private readonly onClick = (event: MouseEvent): void => {
        if (event.defaultPrevented || event.button !== 0) return;
        const target = event.target instanceof Element ? event.target : null;
        const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
        const href = anchor?.getAttribute("href");
        if (!anchor || !href || !this.isPageFile(anchor.href)) return;
        event.preventDefault();
        // opened now, inside the user gesture, so pop-up blockers allow it; filled when the file arrives
        const win = isViewableInBrowser(anchor.href) ? window.open("", "_blank") : null;
        void this.open(anchor.href, win);
    };
}
